import * as React from 'react';
import ReactMoveable, { MoveableProps } from 'react-moveable';
import { memoizeFunction } from '@agilepoint/utils-common';
import { IMoveableProps, IMoveableSurfaceContextProps, IMoveableSurfaceEventManager } from './moveable-types';
import { excludeKeys } from '../../utils';
import { MoveableWrapper } from './parts/moveable-wrapper';
import { withMoveableContext } from './moveable-context';
import { IBaseComponentProps } from '../types';

interface IMoveableBaseProps<T> extends IMoveableProps, Pick<IBaseComponentProps, 'elementRef'> {
    context: IMoveableSurfaceContextProps<T> & IMoveableSurfaceEventManager;
}

type IProxyHandlers = {
    [key in string]: (ev: any) => void;
};

interface IIMoveableState {
    isTargetReady: boolean;
}

const getMergedMoveableProps = memoizeFunction((props: IMoveableBaseProps<{}>) => {
    const { context: { defaultProps } } = props;
    return {
        ...defaultProps,
        ...excludeKeys(props, 'className')
    };
});

class MoveableBase<T = {}> extends React.Component<IMoveableBaseProps<T>, IIMoveableState> {
    static defaultProps: Partial<IMoveableBaseProps<{}>> = {
        highlightMoveable: true
    };

    state: IIMoveableState = {
        isTargetReady: false
    };

    private targetRef: React.RefObject<HTMLElement>;
    private moveableRef = React.createRef<ReactMoveable>();

    private getCallbackProps = memoizeFunction(() => (
        Object.keys(this.props).reduce((callbacks, cbName) => {
            if ((this.props as any)[cbName] instanceof Function) {
                callbacks[cbName] = (ev: any) => this.routeEvent(cbName, ev);
            }
            return callbacks;
        }, {} as IProxyHandlers)
    ));

    constructor(props: IMoveableBaseProps<T>) {
        super(props);
        const { elementRef } = props;
        this.targetRef = elementRef ?? React.createRef<HTMLElement>();
    }

    componentDidMount() {
        const { context: { addUpdateListener } } = this.props;
        addUpdateListener(this.updateMoveable);
        this.setState({
            isTargetReady: true
        });
    }

    componentWillUnmount() {
        const { context: { removeUpdateListener } } = this.props;
        removeUpdateListener(this.updateMoveable);
    }

    render() {
        const { children } = this.props;
        const { isTargetReady } = this.state;
        return (
            <>
                {children(this.targetRef)}
                {isTargetReady ? this.renderMoveable() : null}
            </>
        );
    }

    private renderMoveable() {
        const { context: { containerRef }, className, viewOrigin } = this.props;
        const moveableCallbackProps = this.getCallbackProps();
        const moveableProps = {
            ...getMergedMoveableProps(this.props),
            ...moveableCallbackProps,
            target: this.targetRef.current,
            container: containerRef.current
        };

        return (
            <MoveableWrapper
                className={className}
                highlightMoveable={moveableProps.highlightMoveable}
                viewOrigin={viewOrigin}>
                <ReactMoveable {...moveableProps as MoveableProps} ref={this.moveableRef} />
            </MoveableWrapper>
        );
    }

    private updateMoveable = () => {
        const { current } = this.moveableRef;
        if (current === null) {
            return;
        }
        current.updateRect();
    }

    private routeEvent = (name: string, args: any) => {
        const { context } = this.props;
        const handler = (this.props as any)[name];
        if (handler instanceof Function) {
            handler({ ...args, context });
            context.updateMoveables();
        }
    }
}

export const Moveable = withMoveableContext(MoveableBase);
